import type { ErrorMessage, StreamFormat } from '@auxcast/protocol';
import { DEFAULTS } from '@auxcast/protocol';

import type { PipelineStats } from '../audio/pipeline';
import { AudioPipeline, IDLE_STATS } from '../audio/pipeline';
import { clampJitterMs } from '../audio/jitter';
import type { ConnectionState, StateDetail } from '../net/listenerClient';
import { ListenerClient } from '../net/listenerClient';
import { listenSocketUrl } from '../net/sessionLink';

export interface ListenerState {
  /** True once the user has granted the gesture and audio is wired up. */
  running: boolean;
  /** Audio is up but the AudioContext is not yet running (autoplay policy). */
  audioSuspended: boolean;
  connection: ConnectionState;
  detail: StateDetail;
  name: string;
  live: boolean;
  listeners: number;
  format: StreamFormat | null;
  /** Set when the relay refused us and a human has to fix something. */
  fatal: { code: string; message: string } | null;
  /** The relay wants a passcode, or rejected the one we sent. */
  needsPasscode: boolean;
  passcodeRejected: boolean;
  rttMs: number | null;
  stats: PipelineStats;
  volume: number;
  muted: boolean;
  targetMs: number;
}

export interface ControllerSeed {
  name: string;
  live: boolean;
  listeners: number;
  format: StreamFormat | null;
  requiresPasscode: boolean;
}

const STATS_POLL_MS = 250;
const VOLUME_KEY = 'auxcast.volume';
const BUFFER_KEY = 'auxcast.jitterMs';

function readStored(key: string, fallback: number): number {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: number): void {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* private mode, or storage disabled — the preference just will not persist */
  }
}

/**
 * Owns one listening session: the WebSocket, the audio pipeline, and the state the
 * UI renders. Deliberately framework-free — React subscribes through
 * `useSyncExternalStore`, which keeps StrictMode's double-mount from tearing down
 * a live AudioContext.
 */
export class ListenerController {
  private readonly subscribers = new Set<() => void>();
  private snapshot: ListenerState;
  private client: ListenerClient | null = null;
  private pipeline: AudioPipeline | null = null;
  private statsTimer: ReturnType<typeof setInterval> | null = null;
  private passcode: string | null = null;
  /**
   * Bumped by every `stop()`. Async work started under an old token discards its
   * result, which is what keeps React StrictMode's mount/unmount/mount cycle from
   * leaving an orphaned AudioContext running.
   */
  private startToken = 0;

  constructor(
    private readonly sessionId: string,
    seed: ControllerSeed,
  ) {
    this.snapshot = {
      running: false,
      audioSuspended: false,
      connection: 'idle',
      detail: {},
      name: seed.name,
      live: seed.live,
      listeners: seed.listeners,
      format: seed.format,
      fatal: null,
      needsPasscode: seed.requiresPasscode,
      passcodeRejected: false,
      rttMs: null,
      stats: IDLE_STATS,
      volume: Math.max(0, Math.min(1, readStored(VOLUME_KEY, 1))),
      muted: false,
      targetMs: clampJitterMs(readStored(BUFFER_KEY, DEFAULTS.jitterBufferMs)),
    };
  }

  subscribe = (onChange: () => void): (() => void) => {
    this.subscribers.add(onChange);
    return () => {
      this.subscribers.delete(onChange);
    };
  };

  getSnapshot = (): ListenerState => this.snapshot;

  /**
   * Start listening. Must be called straight from a click or key press: the
   * AudioContext is constructed synchronously here so the browser still counts it
   * as user-initiated.
   */
  async start(passcode?: string | null): Promise<void> {
    if (this.snapshot.running) return;
    this.passcode = passcode ?? this.passcode;
    const token = ++this.startToken;

    let pipeline: AudioPipeline;
    try {
      pipeline = await AudioPipeline.create({
        onFatal: (message) => {
          if (token === this.startToken) this.patch({ fatal: { code: 'audio', message } });
        },
      });
    } catch (error) {
      if (token === this.startToken) {
        this.patch({
          fatal: {
            code: 'audio',
            message: error instanceof Error ? error.message : 'could not start audio',
          },
        });
      }
      return;
    }

    if (token !== this.startToken) {
      void pipeline.close();
      return;
    }

    this.pipeline = pipeline;
    pipeline.setVolume(this.snapshot.volume);
    pipeline.setMuted(this.snapshot.muted);
    pipeline.setTargetMs(this.snapshot.targetMs);

    this.client = new ListenerClient({
      url: listenSocketUrl(this.sessionId),
      passcode: this.passcode,
      handlers: {
        onState: (connection, detail) => this.onConnectionState(connection, detail),
        onFrame: (data) => this.pipeline?.pushFrame(data),
        onSession: (session) => this.patch({ name: session.name, live: session.live }),
        onName: (name) => this.patch({ name }),
        onFormat: (format) => {
          this.pipeline?.setFormat(format);
          this.patch({ format });
        },
        onListeners: (count) => this.patch({ listeners: count }),
        onStreamEnd: () => {
          this.pipeline?.flush();
          this.patch({ live: false });
        },
        onProtocolError: (error) => this.onProtocolError(error),
        onRoundTrip: (rttMs) => this.patch({ rttMs: Math.round(rttMs) }),
      },
    });

    this.patch({
      running: true,
      fatal: null,
      passcodeRejected: false,
      needsPasscode: false,
      audioSuspended: pipeline.suspended,
    });
    this.client.start();
    this.startStatsPolling();
  }

  /** Nudge a context the browser left suspended; needs its own user gesture. */
  async resumeAudio(): Promise<void> {
    await this.pipeline?.resume();
    this.patch({ audioSuspended: this.pipeline?.suspended ?? false });
  }

  /** Stop playback and close the socket, leaving the page usable. Idempotent. */
  stop(): void {
    this.startToken += 1;
    this.client?.stop();
    this.client = null;
    this.stopStatsPolling();

    const pipeline = this.pipeline;
    this.pipeline = null;
    if (pipeline) void pipeline.close();

    this.patch({
      running: false,
      audioSuspended: false,
      connection: 'idle',
      detail: {},
      stats: IDLE_STATS,
      rttMs: null,
    });
  }

  /** Try again after a passcode failure. */
  async retryWithPasscode(passcode: string): Promise<void> {
    this.passcode = passcode;
    this.patch({ fatal: null, passcodeRejected: false });
    if (!this.snapshot.running) {
      await this.start(passcode);
      return;
    }
    this.patch({ needsPasscode: false });
    this.client?.retryWith(passcode);
  }

  setVolume(volume: number): void {
    const clamped = Math.max(0, Math.min(1, volume));
    this.pipeline?.setVolume(clamped);
    writeStored(VOLUME_KEY, clamped);
    this.patch({ volume: clamped });
  }

  setMuted(muted: boolean): void {
    this.pipeline?.setMuted(muted);
    this.patch({ muted });
  }

  setTargetMs(ms: number): void {
    const clamped = clampJitterMs(ms);
    this.pipeline?.setTargetMs(clamped);
    writeStored(BUFFER_KEY, clamped);
    this.patch({ targetMs: clamped });
  }

  /** Meter levels for the current animation frame. Never triggers a re-render. */
  samplePeaks = (atMs: number): { left: number; right: number; holdL: number; holdR: number } =>
    this.pipeline?.sampleMeters(atMs) ?? { left: 0, right: 0, holdL: 0, holdR: 0 };

  private onConnectionState(connection: ConnectionState, detail: StateDetail): void {
    const patch: Partial<ListenerState> = { connection, detail };
    if (connection === 'waiting') patch.live = false;
    if (connection === 'live') patch.live = true;
    if (connection === 'reconnecting' || connection === 'failed') this.pipeline?.flush();
    this.patch(patch);
  }

  private onProtocolError(error: ErrorMessage): void {
    if (error.code === 'passcode-required' || error.code === 'passcode-invalid') {
      this.patch({
        needsPasscode: true,
        passcodeRejected: error.code === 'passcode-invalid',
        fatal: null,
      });
      return;
    }
    if (error.fatal) {
      this.patch({ fatal: { code: error.code, message: error.message } });
    }
  }

  private startStatsPolling(): void {
    this.stopStatsPolling();
    this.statsTimer = setInterval(() => {
      const pipeline = this.pipeline;
      if (!pipeline) return;
      this.patch({ stats: pipeline.stats(), audioSuspended: pipeline.suspended });
    }, STATS_POLL_MS);
  }

  private stopStatsPolling(): void {
    if (this.statsTimer !== null) {
      clearInterval(this.statsTimer);
      this.statsTimer = null;
    }
  }

  private patch(changes: Partial<ListenerState>): void {
    this.snapshot = { ...this.snapshot, ...changes };
    for (const notify of this.subscribers) notify();
  }
}
